import {AfterViewInit, Component, ElementRef, inject, viewChild} from '@angular/core';
import {MatIconButton} from '@angular/material/button';
import {ProjectTypeService} from '../../../service/project-type.service';
import {CantaaErrorHandlerService} from '../../../service/cantaa-error-handler.service';
import {ProjectTypeListItem} from '../../../model/project-type.model';
import {Table, TableLazyLoadEvent, TableModule} from 'primeng/table';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {CantaaMessageService} from '../../../service/cantaa-message.service';
import {FocusHelper} from '../../../service/helper/focus-helper';
import {GridHelperService} from '../../../service/helper/grid-helper.service';
import {GridRequest} from '../../../model/grid-request.model';
import {GridMetaService} from '../../../service/grid-meta.service';
import {Permission} from '../../../constants/Permission';
import {HasPermissionDirective} from '../../../directive/has-permission.directive';
import {CantaaGridMeta} from "../../../model/GridMeta";

@Component({
  selector: 'project-type-grid',
  standalone: true,
  imports: [
    TableModule,
    FormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatIconModule,
    MatIconButton,
    HasPermissionDirective
  ],
  templateUrl: './project-type-grid.component.html',
  styleUrl: './project-type-grid.component.scss'
})
export class ProjectTypeGridComponent implements AfterViewInit {
  dataTable = viewChild.required<Table>('dt');
  readonly gridName = 'PROJECT_TYPE_GRID';
  protected readonly Permission = Permission;

  private gridMetaService = inject(GridMetaService);
  private fb = inject(FormBuilder);
  private messageService = inject(CantaaMessageService);
  private errorHandler = inject(CantaaErrorHandlerService);
  private el = inject(ElementRef);
  private gridHelperService = inject(GridHelperService);
  private projectTypeService = inject(ProjectTypeService);

  projectTypes: ProjectTypeListItem[] = [];
  public totalRecords: number = 0;
  editMode = false;

  gridMeta!: CantaaGridMeta;

  createNewForm = this.fb.group({
    // ce3e0408019016e0
    code: ['', Validators.required],
    name: ['', Validators.required],
  });

  clonedProjectTypes: { [s: number]: ProjectTypeListItem } = {};

  constructor() {
    this.initGridMeta();
  }

  private initGridMeta() {
    const defaultSortMeta = [{ field: 'name', order: 1 }];
    this.gridMeta = this.gridMetaService.getGridMeta(this.gridName, defaultSortMeta);
  }

  async ngAfterViewInit() {
    await this.reloadData();
  }

  onRowEditInit(projectType: ProjectTypeListItem) {
    this.clonedProjectTypes[projectType.id] = { ...projectType };
    this.editMode = true;
  }

  async onRowEditSave(projectType: ProjectTypeListItem) {
    await this.save(projectType);
    this.editMode = false;
    await this.reloadData();
  }

  private async save(projectType: ProjectTypeListItem) {
    try {
      await this.projectTypeService.saveProjectType(projectType);
      this.messageService.success('SAVE.SUCCESS');
    } catch (e) {
      this.errorHandler.handleError(e, 'SAVE.FAILED');
    }
  }

  onRowEditCancel(projectType: ProjectTypeListItem, index: number) {
    this.projectTypes[index] = this.clonedProjectTypes[projectType.id];
    delete this.clonedProjectTypes[projectType.id];
    this.editMode = false;
  }

  async onCreateNew() {
    const projectType = {
      code: this.createNewForm.value.code,
      name: this.createNewForm.value.name,
      active: true
    } as ProjectTypeListItem;

    await this.save(projectType);
    await this.reloadData();
    this.createNewForm.reset({
      code: null,
      name: null
    }, { emitEvent: false });
    Object.values(this.createNewForm.controls).forEach((control) => {
      control?.setErrors(null);
      control?.markAsPristine();
      control?.markAsUntouched();
    });
    FocusHelper.focusFormField(this.el, 'code');
  }

  onCancel() {
    this.createNewForm.reset();
  }

  async onUpdateStatus(projectType: ProjectTypeListItem) {
    try {
      await this.projectTypeService.updateProjectTypeStatus(projectType.id, !projectType.active);
      this.messageService.success('UPDATE.SUCCESS');
      await this.reloadData();
    } catch (e) {
      this.errorHandler.handleError(e, 'UPDATE.FAILED');
    }
  }

  async fetchDataViaTableComponent(lazyLoadMeta: TableLazyLoadEvent) {
    this.gridMetaService.setGridMeta(this.gridName, {lazyLoadMeta});
    let gridRequest = this.gridHelperService.createGridRequest(lazyLoadMeta);
    await this.fetchDAta(gridRequest);
  }

  async reloadData() {
    let lazyLoadMetadata = this.dataTable().createLazyLoadMetadata();
    let gridRequest = this.gridHelperService.createGridRequest(lazyLoadMetadata);
    await this.fetchDAta(gridRequest);
  }

  async fetchDAta(gridRequest: GridRequest) {
    try {
      const collection = await this.projectTypeService.getPagedProjectTypes(gridRequest);
      this.projectTypes = collection.items;
      this.totalRecords = collection.totalNumberOfItems;
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
    }
  }

  getTdClass(item: any) {
    return item.active ? "" : 'strikethrough';
  }
}
